import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import { Music, Search, ArrowRight } from "lucide-react";

export default function Artists() {
  const { t } = useLang();
  const [q, setQ] = useState("");
  const { data: artists = [] } = useQuery({ queryKey: ["artists"], queryFn: async () => (await api.get("/artists")).data });

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? artists.filter((a) => a.name.toLowerCase().includes(s)) : artists;
  }, [q, artists]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const jsonLd = {
    "@context": "https://schema.org", "@type": "ItemList", name: t("artists_title"),
    itemListElement: artists.slice(0, 100).map((a, i) => ({
      "@type": "ListItem", position: i + 1, name: a.name,
      url: `${origin}/artistes/${a.slug || ""}`,
    })),
  };

  return (
    <>
      <Seo title={t("artists_title")} description={t("artists_desc")} canonicalPath="/artistes" jsonLd={jsonLd} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="flex items-center gap-3 mb-3"><Music className="w-7 h-7 text-indigo-400" /><h1 className="font-serif text-3xl sm:text-4xl uppercase tracking-tight text-amber-100">{t("artists_title")}</h1></div>
        <p className="text-stone-400 mb-8 max-w-2xl">{t("artists_desc")}</p>

        <div className="relative max-w-md mb-10">
          <Search className="w-4 h-4 text-stone-500 absolute left-4 top-1/2 -translate-y-1/2" />
          <input data-testid="artists-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("artists_search")}
            className="w-full bg-[#121418] border border-[#272A34] rounded-md pl-11 pr-4 py-3 text-sm text-stone-200 focus:border-indigo-500/50 outline-none" />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="artists-grid">
          {filtered.map((a) => {
            const slug = a.slug || a.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
            return (
              <Link key={a.name} to={`/artistes/${slug}`} data-testid={`artist-card-${slug}`}
                className="group relative overflow-hidden rounded-xl border border-[#272A34] hover:border-indigo-500/50 bg-gradient-to-br from-indigo-950/20 to-[#121418] p-5 transition-all duration-300 hover:-translate-y-1">
                <Music className="w-6 h-6 text-indigo-400/80 mb-4" />
                <div className="font-serif text-lg text-stone-100 group-hover:text-indigo-100 leading-tight line-clamp-2">{a.name}</div>
                {a.genres?.length > 0 && <div className="mt-1 font-mono text-[11px] text-stone-500 line-clamp-1">{a.genres.join(" · ")}</div>}
                <span className="inline-flex items-center gap-1 mt-3 text-xs font-mono text-indigo-300 group-hover:gap-2 transition-all">{t("artists_view")} <ArrowRight className="w-3.5 h-3.5" /></span>
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
}
